/**
 * User search. Spec 11.4: "Search by email/name/ID."
 *
 * Spec 11.5 governs what is NOT here: no reflection text, in the list or anywhere behind
 * it. The server does not return it and this screen has no way to ask for it.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { DataTable, type Column } from '../../../components/ui/DataTable';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Field } from '../../../components/ui/Field';
import { Pagination } from '../../../components/ui/Pagination';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { useAdminUsers, type AdminUserSummary } from '../../../features/admin/api';
import { errorKeyFor } from '../../../lib/errors';
import { useCursorPages } from '../../../lib/pagination';

export const Route = createFileRoute('/$lang/admin/users/')({ component: Page });

function Page(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const { lang } = Route.useParams();

  const [query, setQuery] = useState('');
  const pages = useCursorPages();
  const users = useAdminUsers({ query, cursor: pages.cursor });

  const columns: readonly Column<AdminUserSummary>[] = [
    {
      id: 'name',
      header: t('admin:users.search'),
      cell: (row) => (
        <Link
          to="/$lang/admin/users/$id"
          params={{ lang, id: row.id }}
          className="text-accent underline-offset-4 hover:underline"
        >
          {row.displayName}
        </Link>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      hideOnMobile: true,
      /* A guest has no email; saying so beats an empty cell. */
      cell: (row) => row.email ?? t('admin:users.guest'),
    },
    {
      id: 'role',
      header: t('admin:users.role'),
      cell: (row) => (
        <Badge tone={row.role === 'platform_admin' ? 'warning' : 'neutral'}>{row.role}</Badge>
      ),
    },
    {
      id: 'status',
      header: t('admin:history.status', { defaultValue: 'Status' }),
      cell: (row) =>
        row.suspendedAt === null ? null : <Badge tone="danger">{t('admin:users.suspended')}</Badge>,
    },
    {
      id: 'created',
      header: t('common:dashboard.when'),
      hideOnMobile: true,
      cell: (row) => new Date(row.createdAt).toLocaleDateString(i18n.language),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-ink">{t('admin:nav.users')}</h1>

      <div className="max-w-md">
        <Field
          label={t('admin:users.search')}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            /* A new search is a new list; the old cursors point into the previous one. */
            pages.reset();
          }}
        />
      </div>

      {users.error !== null && <Alert tone="error">{t(errorKeyFor(users.error))}</Alert>}

      {users.isLoading ? (
        <SkeletonList rows={5} label={t('admin:nav.users')} />
      ) : (
        <>
          <DataTable
            caption={t('admin:nav.users')}
            columns={columns}
            rows={users.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={<EmptyState title={t('admin:users.empty')} />}
          />
          <Pagination
            hasPrevious={pages.hasPrevious}
            hasNext={(users.data?.nextCursor ?? null) !== null}
            onPrevious={pages.previous}
            onNext={() => {
              pages.next(users.data?.nextCursor ?? null);
            }}
            labels={{
              previous: t('common:pagination.previous'),
              next: t('common:pagination.next'),
              nav: t('admin:nav.users'),
            }}
          />
        </>
      )}
    </div>
  );
}
